import React from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Enhanced loading skeleton that matches the final AnalysisDashboard layout
 * Provides a more accurate loading experience with proper spacing and structure
 */
export function DashboardLoadingSkeleton({ className, ...props }) {
  const now = new Date();
  const loadingMessage = now.getHours() < 8 
    ? "Preparing today's racing data..." 
    : "Loading performance metrics...";

  return (
    <Card className={cn('p-4 xs:p-6 sm:p-8 border border-[#F30099]/20 shadow-lg bg-white', className)} {...props}>
      {/* Header Section */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex-1">
            {/* Title skeleton */}
            <Skeleton className="h-6 xs:h-8 w-48 xs:w-64 mb-2" />
            
            {/* Subtitle and description skeleton */}
            <div className="flex flex-col xs:flex-row xs:items-center gap-1 xs:gap-2 sm:gap-3">
              <Skeleton className="h-4 xs:h-5 w-32 xs:w-40" />
              <Skeleton className="h-3 xs:h-4 w-48 xs:w-56" />
            </div>
          </div>
          
          {/* Live data indicator skeleton */}
          <div className="flex items-center gap-2 text-xs xs:text-sm">
            <div className="w-1.5 xs:w-2 h-1.5 xs:h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-blue-600 text-xs xs:text-sm">{loadingMessage}</span>
          </div>
        </div>
        
        {/* Description skeleton */}
        <Skeleton className="h-3 xs:h-4 w-full max-w-sm xs:max-w-md mt-2" />
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3 xs:gap-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} className="p-3 xs:p-4 border border-gray-200 bg-gray-50/50">
            <div className="flex items-start justify-between mb-2">
              {/* Icon skeleton */}
              <Skeleton className="w-4 h-4 rounded" />
              
              {/* Trend indicator skeleton */}
              <Skeleton className="w-2 h-2 rounded-full" />
            </div>
            
            {/* Value skeleton */}
            <Skeleton className="h-6 xs:h-7 w-16 xs:w-20 mb-1" />
            
            {/* Title skeleton */}
            <Skeleton className="h-3 xs:h-4 w-full mb-1" />
            
            {/* Subtitle skeleton */}
            <Skeleton className="h-2 xs:h-3 w-3/4" />
          </Card>
        ))}
      </div>

      {/* Footer Section */}
      <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          {/* Context information skeleton */}
          <div className="flex flex-col xs:flex-row xs:flex-wrap gap-2 xs:gap-4 sm:gap-6">
            <Skeleton className="h-3 xs:h-4 w-24 xs:w-32" />
            <Skeleton className="h-3 xs:h-4 w-32 xs:w-40" />
            <Skeleton className="h-3 xs:h-4 w-28 xs:w-36" />
          </div>
          
          {/* Last updated skeleton */}
          <div className="flex flex-col xs:flex-row xs:items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-3 w-32 xs:w-40" />
          </div>
        </div>
        
        {/* Data freshness indicator skeleton */}
        <div className="mt-2 xs:mt-3">
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
            <Skeleton className="h-3 w-32 xs:w-40" />
          </div>
        </div>
      </div>
    </Card>
  );
}

export default DashboardLoadingSkeleton;